import { useState } from "react";

export const landmarks = [
  { name: "Fort Lauderdale-Hollywood Intl (FLL)", icon: "ri-flight-takeoff-line", distance: "Primary airport — within Fort Lauderdale city limits", desc: "Major South Florida international airport serving Southwest, JetBlue, Spirit, Delta, and international carriers. The primary departure point for Fort Lauderdale cruise passengers and Caribbean-bound travelers.", url: "https://www.broward.org/airport/", schemaType: "Airport" },
  { name: "Port Everglades", icon: "ri-anchor-line", distance: "~3 miles — southeastern Fort Lauderdale", desc: "One of the busiest cruise ports in the world, hosting Royal Caribbean, Celebrity, Princess, Holland America, and Carnival cruise lines. A defining travel hub for Fort Lauderdale and Broward County.", url: "https://www.porteverglades.net/", schemaType: "TouristAttraction" },
  { name: "Las Olas Boulevard", icon: "ri-store-2-line", distance: "Downtown Fort Lauderdale", desc: "Iconic shopping, dining, and entertainment district lined with palm trees, galleries, boutiques, and waterfront restaurants. The cultural heart of Fort Lauderdale.", url: "https://www.lasolasboulevard.com/", schemaType: "TouristAttraction" },
  { name: "Fort Lauderdale Beach", icon: "ri-sun-line", distance: "~2 miles — along A1A", desc: "Seven miles of award-winning Atlantic coastline with Blue Wave certified beaches, oceanfront promenade, and resorts. A major South Florida tourism and recreation destination.", url: "https://www.visitlauderdale.com/beaches/", schemaType: "TouristAttraction" },
  { name: "Riverwalk Fort Lauderdale", icon: "ri-water-flash-line", distance: "Downtown — along New River", desc: "Linear waterfront park in downtown Fort Lauderdale connecting arts venues, restaurants, parks, and museums along the New River. Home to the Broward Center for the Performing Arts.", url: "https://www.goriverwalk.com/", schemaType: "TouristAttraction" },
  { name: "Greater Fort Lauderdale Convention Center", icon: "ri-building-line", distance: "~1 mile — Port Everglades area", desc: "Massive waterfront convention facility hosting major trade shows, corporate events, and international conferences. A key driver of business travel to Fort Lauderdale.", url: "https://www.ftlauderdalecc.com/", schemaType: "TouristAttraction" },
  { name: "Miami International Airport (MIA)", icon: "ri-flight-takeoff-line", distance: "~30 miles south via I-95", desc: "Major international gateway with the most long-haul and Latin American routes in South Florida. A common alternative for Fort Lauderdale travelers to destinations not served by FLL.", url: "https://www.miami-airport.com/", schemaType: "Airport" },
  { name: "Sawgrass Mills Mall", icon: "ri-store-2-line", distance: "~12 miles west — Sunrise, FL", desc: "Largest outlet and value retail shopping destination in the United States with over 350 stores. A major tourist attraction drawing visitors from across Florida and Latin America.", url: "https://www.simon.com/mall/sawgrass-mills", schemaType: "TouristAttraction" },
  { name: "Interstate 95 (I-95)", icon: "ri-road-map-line", distance: "Runs directly through Fort Lauderdale", desc: "Primary north-south highway connecting Fort Lauderdale to Miami, West Palm Beach, and the entire Eastern Seaboard. Essential for airport access and regional travel.", url: "https://www.fdot.gov/", schemaType: "CivicStructure" },
];

const photoLocations = [
  { name: "CVS Pharmacy — Las Olas Blvd", address: "1400 E Las Olas Blvd, Fort Lauderdale, FL 33301", hours: "Daily 7 AM – 11 PM" },
  { name: "CVS Pharmacy — Federal Hwy", address: "1600 N Federal Hwy, Fort Lauderdale, FL 33304", hours: "Daily 7 AM – 11 PM" },
  { name: "CVS Pharmacy — Wilton Manors", address: "2605 N Federal Hwy, Fort Lauderdale, FL 33306", hours: "Daily 7 AM – 11 PM" },
  { name: "Walgreens — Federal Hwy", address: "2000 N Federal Hwy, Fort Lauderdale, FL 33305", hours: "Call for hours" },
  { name: "Walgreens — Sunrise Blvd", address: "1401 W Sunrise Blvd, Fort Lauderdale, FL 33311", hours: "Call for hours" },
  { name: "The UPS Store — Las Olas", address: "900 E Las Olas Blvd, Fort Lauderdale, FL 33301", hours: "Call for hours" },
  { name: "FedEx Office — Federal Hwy", address: "1800 N Federal Hwy, Fort Lauderdale, FL 33305", hours: "Call for hours" },
  { name: "Walmart Photo Center — Fort Lauderdale", address: "2500 W Broward Blvd, Fort Lauderdale, FL 33312", hours: "Call for hours" },
  { name: "Walmart Photo Center — Oakland Park", address: "3001 W Oakland Park Blvd, Fort Lauderdale, FL 33311", hours: "Call for hours" },
  { name: "Rite Aid — Federal Hwy", address: "2200 N Federal Hwy, Fort Lauderdale, FL 33305", hours: "Call for hours" },
];

const dmvLocations = [
  {
    name: "Broward County Tax Collector — Fort Lauderdale",
    address: "115 S Andrews Ave, Fort Lauderdale, FL 33301",
    hours: "Mon–Fri, 8:30 AM – 5:00 PM",
    note: "Driver licenses, REAL ID, titles & registration. Appointment recommended.",
    featured: true,
  },
  {
    name: "Broward County Tax Collector — Oakland Park",
    address: "1401 W Oakland Park Blvd, Fort Lauderdale, FL 33311",
    hours: "Mon–Fri, 8:30 AM – 5:00 PM",
    note: "Northern Fort Lauderdale option. Appointment recommended.",
    featured: false,
  },
  {
    name: "Broward County Tax Collector — Plantation",
    address: "10041 Pines Blvd, Plantation, FL 33324",
    hours: "Mon–Fri, 8:30 AM – 5:00 PM",
    note: "Western Broward County option. Appointment recommended.",
    featured: false,
  },
];

export default function FortLauderdaleHelpfulResourcesSection() {
  const [photoExpanded, setPhotoExpanded] = useState(false);
  const visiblePhotos = photoExpanded ? photoLocations : photoLocations.slice(0, 6);

  return (
    <section id="local-resources" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Resources</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Helpful Resources for Fort Lauderdale Travelers
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Getting your passport ready involves more than just the application. Here are local Fort Lauderdale resources to help you gather everything you need.
          </p>
        </div>

        <div className="space-y-6">

          {/* Passport Photos */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-camera-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Passport Photos</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Passport Photo Locations in Fort Lauderdale</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-2">
                Your passport application requires two identical 2&quot;×2&quot; passport photos taken within the last 6 months. Popular passport photo locations in Fort Lauderdale include:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                {visiblePhotos.map((loc, i) => (
                  <div key={i} className="bg-[#F7F8FA] border border-gray-200 rounded-lg p-3.5">
                    <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{loc.name}</p>
                    <p className="text-gray-500 text-xs leading-relaxed">{loc.address}</p>
                    <p className="text-gray-400 text-xs mt-1">{loc.hours}</p>
                  </div>
                ))}
              </div>
              {!photoExpanded && (
                <button
                  onClick={() => setPhotoExpanded(true)}
                  className="text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs flex items-center gap-1.5 mb-4 cursor-pointer transition-colors"
                >
                  <i className="ri-add-circle-line text-sm"></i>
                  Show all {photoLocations.length} photo locations
                </button>
              )}
              <div className="flex flex-wrap gap-4 mb-4">
                <a href="https://travel.state.gov/content/travel/en/passports/how-apply/photos.html" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                  <i className="ri-external-link-line text-sm"></i>U.S. State Dept. Official Photo Requirements
                </a>
              </div>
              <div className="bg-[#F7F8FA] border border-gray-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-lightbulb-line text-[#F6C344] mr-1.5"></i>
                  <strong>Tip:</strong> Wear a plain white or off-white top, no glasses, neutral expression. Photos must meet strict U.S. State Department specifications — ask the technician to confirm compliance before you leave.
                </p>
              </div>
            </div>
          </div>

          {/* Vital Records */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-file-text-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Vital Records</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Birth, Death, Marriage &amp; Divorce Records — Broward County</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                If you need a certified copy of your birth certificate for a passport application, a marriage certificate for a name change, or a divorce decree to support your passport correction, here are the official sources for vital records in Broward County. Most records can be ordered online for delivery within 5–10 business days.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                {[
                  { icon: "ri-user-smile-line", label: "Birth Certificate", desc: "Request certified birth certificates from the Florida Department of Health in Broward County. Online, mail, or in-person options available.", url: "https://broward.floridahealth.gov/" },
                  { icon: "ri-file-list-2-line", label: "Death Certificate", desc: "Obtain certified death certificates through the Florida Department of Health in Broward County. Required for estate and legal matters.", url: "https://broward.floridahealth.gov/" },
                  { icon: "ri-hearts-line", label: "Marriage Certificate", desc: "Request marriage license records from the Broward County Clerk of Courts. Certified copies available for name changes and legal purposes.", url: "https://www.browardclerk.org/" },
                  { icon: "ri-file-copy-line", label: "Divorce Decree", desc: "Obtain certified divorce records through the Broward County Clerk of Courts. Required for name change passport corrections and legal documentation.", url: "https://www.browardclerk.org/" },
                ].map((record, i) => (
                  <a
                    key={i}
                    href={record.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-3.5 bg-[#F7F8FA] border border-gray-200 rounded-lg p-4 hover:border-[#F6C344] transition-all group"
                  >
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#EEF2F8] flex-shrink-0 mt-0.5">
                      <i className={`${record.icon} text-[#1A365D] text-base group-hover:text-[#F6C344] transition-colors`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{record.label}</p>
                      <p className="text-gray-500 text-xs leading-relaxed mb-1.5">{record.desc}</p>
                      <span className="inline-flex items-center gap-1 text-[#1A365D] font-semibold text-xs group-hover:text-[#F6C344] transition-colors">
                        <i className="ri-external-link-line text-xs"></i>Get Records
                      </span>
                    </div>
                  </a>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-information-line text-amber-500 mr-1.5"></i>
                  <strong>Don&apos;t see your county?</strong> If the record you need was issued outside Broward County, visit <a href="https://www.vitalchek.com/" target="_blank" rel="noopener noreferrer" className="font-bold text-[#1A365D] underline underline-offset-2 hover:text-[#F6C344] transition-colors">VitalChek.com</a> — the official online service for ordering vital records nationwide across all U.S. states and counties.
                </p>
              </div>
            </div>
          </div>

          {/* DMV */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-id-card-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Driver&apos;s License &amp; State ID</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Broward County Tax Collector — DMV Services</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                A valid government-issued photo ID is required for your passport application. If you need a new or renewed Florida driver&apos;s license or REAL ID, the Broward County Tax Collector handles DMV services in the Fort Lauderdale area.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
                {dmvLocations.map((loc, i) => (
                  <div key={i} className={`rounded-lg p-4 border ${loc.featured ? "bg-amber-50 border-[#F6C344]/40" : "bg-[#F7F8FA] border-gray-200"}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-map-pin-2-line text-[#1A365D] text-base"></i>
                      </div>
                      <div>
                        <p className="font-bold text-[#1A365D] text-xs leading-snug">{loc.name}</p>
                        <p className="text-gray-500 text-xs mt-1">{loc.address}</p>
                        <p className="text-gray-400 text-xs mt-0.5">{loc.hours}</p>
                        <p className="text-[#1A365D] text-xs font-medium mt-1.5 leading-snug">{loc.note}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-4 mb-4">
                <a href="https://www.broward.org/TaxCollector/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                  <i className="ri-external-link-line text-sm"></i>Broward County Tax Collector — DMV Services
                </a>
                <a href="https://www.flhsmv.gov/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                  <i className="ri-external-link-line text-sm"></i>FLHSMV — Florida DMV
                </a>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-calendar-check-line text-amber-500 mr-1.5"></i>
                  <strong>Appointment recommended</strong> — walk-in wait times can be long. Book your slot online at the Broward County Tax Collector website before visiting.
                </p>
              </div>
            </div>
          </div>

          {/* Airports */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Airports</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Airports Serving Fort Lauderdale Travelers</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                Fort Lauderdale is uniquely positioned between three major South Florida airports. Fort Lauderdale-Hollywood International (FLL) is the primary and closest option, while Miami International (MIA) and Palm Beach International (PBI) provide additional route coverage.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
                {[
                  {
                    code: "FLL",
                    name: "Fort Lauderdale-Hollywood International Airport",
                    distance: "Primary airport — located within Fort Lauderdale city limits",
                    address: "100 Terminal Dr, Fort Lauderdale, FL 33315",
                    note: "Shares Fort Lauderdale's name and is the most convenient airport. Major hub for Southwest, JetBlue, and Spirit with direct Caribbean, Mexico, and Latin America service.",
                    flightUrl: "https://www.broward.org/airport/",
                    flightLabel: "FLL Airport Website",
                    featured: true,
                  },
                  {
                    code: "MIA",
                    name: "Miami International Airport",
                    distance: "Secondary airport — 25–30 min south via I-95",
                    address: "2100 NW 42nd Ave, Miami, FL 33142",
                    note: "Major international hub with the most long-haul and Latin American routes. Backup option when FLL does not serve your destination.",
                    flightUrl: "https://www.miami-airport.com/",
                    flightLabel: "MIA Airport Website",
                    featured: false,
                  },
                  {
                    code: "PBI",
                    name: "Palm Beach International Airport",
                    distance: "Tertiary airport — 45–50 min north via I-95",
                    address: "1000 Turnage Blvd, West Palm Beach, FL 33406",
                    note: "Smaller, efficient airport for certain domestic and seasonal international routes. Alternative when FLL and MIA are congested.",
                    flightUrl: "https://www.pbia.org/",
                    flightLabel: "PBI Airport Website",
                    featured: false,
                  },
                ].map((airport, i) => (
                  <div key={i} className={`rounded-lg p-4 border ${airport.featured ? "bg-amber-50 border-[#F6C344]/40" : "bg-[#F7F8FA] border-gray-200"}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[#1A365D] font-black text-xl leading-none">{airport.code}</span>
                      {airport.featured && (
                        <span className="text-xs font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Primary</span>
                      )}
                    </div>
                    <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{airport.name}</p>
                    <div className="flex items-center gap-1.5 text-xs text-[#F6C344] font-semibold mb-2">
                      <i className="ri-road-map-line"></i>
                      <span>{airport.distance}</span>
                    </div>
                    <p className="text-gray-500 text-xs leading-relaxed mb-1">{airport.address}</p>
                    <p className="text-gray-400 text-xs leading-relaxed mb-3">{airport.note}</p>
                    <a href={airport.flightUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                      <i className="ri-external-link-line text-sm"></i>{airport.flightLabel}
                    </a>
                  </div>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-passport-line text-amber-500 mr-1.5"></i>
                  <strong>Passport required for all international flights.</strong> If your departure is within two weeks, standard processing won&apos;t make it in time — <a href="https://fastpassportcenter.com/checkout/" className="font-bold text-[#1A365D] underline underline-offset-2 hover:text-[#F6C344] transition-colors">expedited processing through Fast Passport Center</a> is your most reliable option.
                </p>
              </div>
            </div>
          </div>

          {/* Nearby Landmarks */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-landscape-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Nearby Landmarks</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Key Landmarks &amp; Transportation Hubs Near Fort Lauderdale</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                Fort Lauderdale is a major South Florida travel corridor anchored by the Fort Lauderdale-Hollywood International Airport, Port Everglades cruise terminal, and the iconic Las Olas Boulevard. These landmarks define travel patterns for Broward County residents and visitors — from cruise passengers sailing out of Port Everglades to business travelers attending conferences at the Convention Center.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-5">
                {landmarks.map((landmark, i) => (
                  <a
                    key={i}
                    href={landmark.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-[#F7F8FA] border border-gray-200 rounded-lg p-4 hover:border-[#F6C344] transition-all group"
                  >
                    <div className="flex items-center gap-2.5 mb-2.5">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#EEF2F8] flex-shrink-0 group-hover:bg-[#F6C344]/15 transition-colors">
                        <i className={`${landmark.icon} text-[#1A365D] text-sm`}></i>
                      </div>
                      <div>
                        <p className="font-bold text-[#1A365D] text-xs leading-snug">{landmark.name}</p>
                        <p className="text-gray-400 text-[11px]">{landmark.distance}</p>
                      </div>
                    </div>
                    <p className="text-gray-500 text-xs leading-relaxed">{landmark.desc}</p>
                  </a>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-passport-line text-amber-500 mr-1.5"></i>
                  <strong>Passport required for all international travel.</strong> Whether you are sailing from Port Everglades on a Caribbean cruise, departing FLL for Mexico, or flying out of MIA to Latin America — a valid U.S. passport is mandatory. <a href="https://fastpassportcenter.com/checkout/" className="font-bold text-[#1A365D] underline underline-offset-2 hover:text-[#F6C344] transition-colors">Expedited processing through Fast Passport Center</a> is the fastest option for Fort Lauderdale residents.
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}